import React, { useMemo, useRef, useState } from 'react';
import { useDynamicVisibleItemsLimit } from '@fabric-ui/components';
import { flex, getCssVarColorWithOpacity } from '@fabric-ui/theme';
import { ellipsis } from '@fabric-ui/utils';
import { Box, Paper, Popper } from '@mui/material';
import { IconVariant, ItemWithLogo, Logo } from './ItemWithLogo';

interface TooltipContentProps {
  variant: IconVariant;
  items: string[];
  limit: number;
  size: IconSize;
  isAvatar?: boolean;
  shouldShowTooltip: boolean;
  CustomItemRender?: any;
  avatarSize?: number;
}

export enum IconSize {
  medium = 'medium',
  small = 'small',
  collapsed = 'collapsed',
  collapsedMedium = 'collapsedMedium',
  collapsedXSmall = 'collapsedXSmall',
  collapsedXXSmall = 'collapsedXXSmall',
}

const avatarSizes = {
  [IconSize.medium]: 11,
  [IconSize.small]: 8,
  [IconSize.collapsed]: 11,
  [IconSize.collapsedMedium]: 12,
  [IconSize.collapsedXSmall]: 8,
  [IconSize.collapsedXXSmall]: 6,
};

const TooltipContent: React.FC<TooltipContentProps> = ({
  items,
  limit,
  variant,
  size,
  isAvatar = false,
  CustomItemRender,
  shouldShowTooltip,
  avatarSize,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = ({ currentTarget }) => setAnchorEl(currentTarget);
  const handleMouseLeave = () => setAnchorEl(null);

  return (
    <Box
      onMouseLeave={handleMouseLeave}
      onClick={e => e.stopPropagation()}
      sx={{ backgroundColor: theme => theme.palette.white.main, border: `2px solid var(--color-border-inverted-primary)` }}>
      <Box key="source-overflow" onMouseOver={handleClick} sx={{ flexShrink: 0 }}>
        <Logo
          transparent
          style={{
            minWidth: 'max-content',
            backgroundColor: getCssVarColorWithOpacity('var(--color-content-immutable-lightGrey)', 0.28, '#ffffff'),
            width: size === IconSize.small ? 24 : size === IconSize.collapsedMedium ? 38 : 30,
            height: getIconSize(size),
            borderRadius: 15,
            flexShrink: 0,
          }}>
          <Box
            sx={{
              fontSize: size === IconSize.small ? 12 : 13,
              fontWeight: '600',
              color: 'var(--color-content-immutable-grey)',
            }}>
            +{items.length - limit}
          </Box>
        </Logo>
      </Box>
      {shouldShowTooltip && (
        <Popper
          key="source-popover"
          placement="bottom-start"
          anchorEl={anchorEl}
          open={!!anchorEl}
          sx={{
            maxHeight: '600px',
            overflowY: 'auto',
            boxShadow: (theme: any) => theme.components?.MuiPaper?.styleOverrides?.root?.boxShadow,
            zIndex: theme => theme.zIndex.modal,
          }}>
          <Paper sx={{ p: 2, ...flex.col, gap: 2 }}>
            {items
              .slice(limit, items.length)
              .map(
                item =>
                  (CustomItemRender && <CustomItemRender key={item} item={item} variant={variant} isAvatar={isAvatar} />) || (
                    <ItemWithLogo key={item} type={item} variant={variant} isAvatar={isAvatar} avatarSize={avatarSize} />
                  )
              )}
          </Paper>
        </Popper>
      )}
    </Box>
  );
};

export const itemWithLogoTableCellStyle = { '&.MuiTableCell-root.MuiTableCell-body': { py: '10px' } };

interface AvItemWithLogoGroupProps {
  variant: IconVariant;
  items?: string[];
  limit?: number;
  size?: IconSize;
  isAvatar?: boolean;
  shouldShowTooltip?: boolean;
  sx?: Record<string, any>;
  CustomItemRender?: any;
  inputVariantSpace?: number;
  hideIconText?: boolean;
}

const getIconSize = (size: IconSize) =>
  size === IconSize.collapsedXXSmall
    ? 10
    : size === IconSize.collapsedXSmall
      ? 18
      : size === IconSize.small
        ? 20
        : size === IconSize.collapsedMedium
          ? 32
          : 24;

const AvItemWithLogoGroup: React.FC<AvItemWithLogoGroupProps> = ({
  items = [],
  variant,
  limit: limitFromProps,
  size = IconSize.small,
  isAvatar = false,
  sx = {},
  CustomItemRender,
  shouldShowTooltip = true,
  inputVariantSpace,
  hideIconText = false,
}) => {
  const rootRef = useRef<HTMLElement>(null);
  const hideText =
    hideIconText || [IconSize.collapsedMedium, IconSize.collapsed, IconSize.collapsedXSmall, IconSize.collapsedXXSmall].includes(size);

  const variantSpace = useMemo(
    () =>
      inputVariantSpace ??
      (size === IconSize.collapsed && variant === IconVariant.color
        ? 8
        : size === IconSize.collapsedMedium
          ? 10
          : size === IconSize.collapsedXSmall
            ? 6
            : size === IconSize.collapsedXXSmall
              ? 4
              : 4),
    [inputVariantSpace, size, variant]
  );

  const limit = useDynamicVisibleItemsLimit(rootRef, {
    fixedLimit: limitFromProps,
    items,
    overlapBetweenItems: hideText ? 0 : variantSpace,
    parentElement: rootRef.current?.parentElement || null,
  });

  const hasOverflow = items.length > limit;
  const shownItems = hasOverflow ? items.slice(0, limit) : items;

  return (
    <Box
      ref={rootRef}
      sx={{
        ...flex.row,
        ...(hideText
          ? {
              ...(items.length > 1 && { pl: `${variantSpace}px` }),
              '> div': {
                alignSelf: 'center',
                position: 'relative',
                border: theme => `${getIconSize(size) > 10 ? 2 : 1}px solid ${theme.palette.white.main}`,
                borderRadius: '50px',
                ...(items.length > 1 && { ml: `-${variantSpace}px` }),
                flexShrink: 0,
              },
            }
          : { gap: 2 }),
        ...sx,
      }}>
      {shownItems.map((value, index) => (
        <Box key={value || index} sx={{ flexShrink: 0, maxWidth: '100%' }}>
          {(CustomItemRender && <CustomItemRender item={value} size={getIconSize(size)} />) || (
            <ItemWithLogo
              variant={variant}
              hideText={hideText}
              textStyle={ellipsis}
              logoWidth={getIconSize(size)}
              type={value}
              isAvatar={isAvatar}
              avatarSize={avatarSizes[size]}
            />
          )}
        </Box>
      ))}
      {hasOverflow && (
        <TooltipContent
          limit={limit}
          items={items}
          variant={variant}
          size={size}
          isAvatar={isAvatar}
          CustomItemRender={CustomItemRender}
          shouldShowTooltip={shouldShowTooltip}
        />
      )}
    </Box>
  );
};

export default AvItemWithLogoGroup;
